import React, { useState, useEffect, useMemo } from 'react';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  Chip,
  Fade,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  alpha,
  useTheme,
  Skeleton,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';
import QuadrimestreSelector, { getQuadrimestreAtual, formatQuadrimestreParam } from '../../components/saude/QuadrimestreSelector';
import {
  VALORES_QUALIDADE,
  VALORES_VINCULO,
  VALORES_FIXO,
  CLASSIFICACOES,
  Classificacao,
  getValorMensal,
} from '../../data/valores-financeiros';
import { buildFinanceiroContext } from '../../utils/saude-context-text';

const CLASSIFICACAO_COLORS: Record<Classificacao, string> = {
  Otimo: '#2e7d32',
  Bom: '#1565c0',
  Suficiente: '#e65100',
  Regular: '#c62828',
};

const formatCurrency = (value: number): string =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 });

const SaudeFinanceiro: React.FC = () => {
  const theme = useTheme();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [quadrimestre, setQuadrimestre] = useState(getQuadrimestreAtual().quadrimestre);
  const [ano, setAno] = useState(getQuadrimestreAtual().ano);

  // Simulacao what-if: override de classificacao por grupo
  const [simClassificacoes, setSimClassificacoes] = useState<Record<string, number>>({});
  // Estrato IED (1=mais vulneravel/maior valor, 4=menos vulneravel/menor valor)
  const [estratoIED, setEstratoIED] = useState(4);

  useEffect(() => {
    if (!municipioAtivo?.id) return;
    setLoading(true);
    setError(null);
    const qParam = formatQuadrimestreParam(quadrimestre, ano);
    saudeService.getSimulacaoFinanceira(municipioAtivo.id, qParam, unidadeSelecionada?.codunidade)
      .then((res: any) => {
        setData(res);
        // Inicializar simulacao com classificacoes atuais
        const initial: Record<string, number> = {};
        if (res.classificacoes) {
          for (const [grupo, classif] of Object.entries(res.classificacoes)) {
            initial[grupo] = CLASSIFICACOES.indexOf(classif as Classificacao);
          }
        }
        setSimClassificacoes(initial);
      })
      .catch((err: any) => setError(err.response?.data?.message || 'Erro ao carregar simulacao financeira'))
      .finally(() => setLoading(false));
  }, [municipioAtivo?.id, quadrimestre, ano, unidadeSelecionada?.codunidade]);

  // Calcular receita simulada
  const simulacao = useMemo(() => {
    if (!data) return null;

    let receitaSimuladaQualidade = 0;
    let receitaSimuladaVinculo = 0;
    let receitaSimuladaFixo = 0;

    for (const comp of data.componentes || []) {
      const isFixo = comp.componente?.includes('Fixo');
      for (const eq of comp.equipes || []) {
        if (isFixo) {
          // Fixo: calculado por estrato IED selecionado
          const valorIED = VALORES_FIXO[eq.tipo]?.[estratoIED] ?? eq.valorUnitario ?? 0;
          receitaSimuladaFixo += valorIED * eq.quantidade;
          continue;
        }

        let grupo = 'esf-eap';
        if (eq.tipoMs === '71') grupo = 'esb';
        else if (eq.tipoMs === '72') grupo = 'emulti';

        const simIdx = simClassificacoes[grupo] ?? 0;
        const simClass = CLASSIFICACOES[simIdx] || 'Regular';

        const tabela = comp.componente.includes('Vinculo') ? VALORES_VINCULO : VALORES_QUALIDADE;
        const valorSim = getValorMensal(tabela, eq.tipo, simClass);
        const receitaSim = valorSim * eq.quantidade;

        if (comp.componente.includes('Vinculo')) {
          receitaSimuladaVinculo += receitaSim;
        } else {
          receitaSimuladaQualidade += receitaSim;
        }
      }
    }

    const receitaSimTotal = receitaSimuladaQualidade + receitaSimuladaVinculo + receitaSimuladaFixo;
    const receitaAtual = data.resumo?.receitaMensal || 0;
    const delta = receitaSimTotal - receitaAtual;

    return { receitaSimTotal, delta, receitaSimuladaQualidade, receitaSimuladaVinculo, receitaSimuladaFixo };
  }, [data, simClassificacoes, estratoIED]);

  // Leitura do delta em 3 estados (ganho / perda / igual ao atual).
  // Sempre renderizado para que o card nao mude de altura durante o arraste dos sliders.
  const deltaInfo = useMemo(() => {
    const d = simulacao?.delta ?? 0;
    if (d > 0) {
      return {
        chipColor: 'success' as const,
        chipLabel: `+${formatCurrency(d)}/mes`,
        chipIcon: <TrendingUpIcon />,
        severity: 'success' as const,
        mensagem: (
          <>Ganho de ate <strong>{formatCurrency(d)}/mes</strong> ({formatCurrency(d * 12)}/ano) em cofinanciamento federal.</>
        ),
      };
    }
    if (d < 0) {
      return {
        chipColor: 'error' as const,
        chipLabel: `${formatCurrency(d)}/mes`,
        chipIcon: <TrendingDownIcon />,
        severity: 'warning' as const,
        mensagem: (
          <>Perda de ate <strong>{formatCurrency(Math.abs(d))}/mes</strong> ({formatCurrency(Math.abs(d) * 12)}/ano) em cofinanciamento federal.</>
        ),
      };
    }
    return {
      chipColor: 'default' as const,
      chipLabel: 'Sem variacao',
      chipIcon: <RemoveIcon />,
      severity: 'info' as const,
      mensagem: <>Cenario igual ao atual — mova os controles para simular o impacto financeiro.</>,
    };
  }, [simulacao]);

  const dadosContexto = useMemo(() => {
    if (!data) return undefined;
    return buildFinanceiroContext(data);
  }, [data]);

  // Gap chart data
  const gapChartData = useMemo(() => {
    if (!data?.componentes) return [];
    return data.componentes.map((c: any) => ({
      name: c.componente.replace(' e Acompanhamento', ''),
      atual: c.totalMensal,
      gap: c.totalMaximo - c.totalMensal,
    }));
  }, [data]);

  if (!municipioAtivo?.id) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Selecione um municipio para acessar o simulador financeiro.</Alert>
      </Box>
    );
  }

  return (
    <>
    <Fade in>
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
        {/* Header - always visible */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              <AttachMoneyIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#2e7d32' }} />
              Simulador Financeiro
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cofinanciamento federal da APS (Portaria GM/MS 3.493/2024)
            </Typography>
          </Box>
          <QuadrimestreSelector
            quadrimestre={quadrimestre}
            ano={ano}
            onChange={(q, a) => { setQuadrimestre(q); setAno(a); }}
          />
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map(i => (
              <Grid item xs={12} md={6} key={i}><Skeleton variant="rounded" height={200} /></Grid>
            ))}
          </Grid>
        ) : data && (
          <>
            {/* Cards Resumo */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Receita Mensal Atual</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ color: '#1565c0' }}>
                      {formatCurrency(data.resumo.receitaMensal)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrency(data.resumo.receitaAnual)}/ano
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Receita Maxima Potencial</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ color: '#2e7d32' }}>
                      {formatCurrency(data.resumo.receitaMaxima)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrency(data.resumo.receitaMaximaAnual)}/ano
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Gap Mensal</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ color: '#c62828' }}>
                      {formatCurrency(data.resumo.gap)}
                    </Typography>
                    <Chip
                      size="small"
                      icon={<WarningAmberIcon />}
                      label={`${formatCurrency(data.resumo.gapAnual)}/ano perdido`}
                      color="error"
                      variant="outlined"
                      sx={{ mt: 0.5 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Aproveitamento</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ color: data.resumo.receitaMaxima > 0 ? '#e65100' : '#9e9e9e' }}>
                      {data.resumo.receitaMaxima > 0
                        ? `${Math.round((data.resumo.receitaMensal / data.resumo.receitaMaxima) * 100)}%`
                        : '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      do potencial maximo
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Gap Chart */}
            <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Gap Financeiro por Componente</Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={gapChartData} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(v: number) => `R$${(v / 1000).toFixed(0)}k`} />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value: any) => formatCurrency(Number(value))} />
                    <Legend />
                    <Bar dataKey="atual" name="Receita Atual" fill="#1565c0" stackId="a" />
                    <Bar dataKey="gap" name="Gap" fill={alpha('#c62828', 0.3)} stackId="a" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Detalhamento por Componente */}
            <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Detalhamento por Tipo de Equipe</Typography>
                {data.componentes.map((comp: any) => (
                  <Box key={comp.componente} sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'text.secondary' }}>
                      {comp.componente}
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Tipo</TableCell>
                            <TableCell align="center">Equipes</TableCell>
                            <TableCell align="center">Classificacao</TableCell>
                            <TableCell align="right">Valor/Equipe</TableCell>
                            <TableCell align="right">Receita Mensal</TableCell>
                            <TableCell align="right">Valor Max</TableCell>
                            <TableCell align="right">Receita Max</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {comp.equipes.map((eq: any) => (
                            <TableRow key={`${comp.componente}-${eq.tipo}`}>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>{eq.tipo}</Typography>
                              </TableCell>
                              <TableCell align="center">{eq.quantidade}</TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={eq.classificacao}
                                  size="small"
                                  sx={{
                                    backgroundColor: alpha(CLASSIFICACAO_COLORS[eq.classificacao as Classificacao] || '#9e9e9e', 0.1),
                                    color: CLASSIFICACAO_COLORS[eq.classificacao as Classificacao] || '#9e9e9e',
                                    fontWeight: 600,
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right">{formatCurrency(eq.valorUnitario)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(eq.receitaMensal)}</TableCell>
                              <TableCell align="right" sx={{ color: 'text.secondary' }}>{formatCurrency(eq.valorMaximo)}</TableCell>
                              <TableCell align="right" sx={{ color: '#2e7d32' }}>{formatCurrency(eq.receitaMaxima)}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                            <TableCell colSpan={4}><Typography variant="body2" fontWeight={700}>Total</Typography></TableCell>
                            <TableCell align="right"><Typography variant="body2" fontWeight={700}>{formatCurrency(comp.totalMensal)}</Typography></TableCell>
                            <TableCell />
                            <TableCell align="right"><Typography variant="body2" fontWeight={700} sx={{ color: '#2e7d32' }}>{formatCurrency(comp.totalMaximo)}</Typography></TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}
              </CardContent>
            </Card>

            {/* Simulador What-If */}
            <Card elevation={0} sx={{ borderRadius: 3, border: `2px solid ${theme.palette.primary.main}`, mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700} color="primary">
                      Simulador What-If
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ajuste as classificacoes para simular o impacto financeiro
                    </Typography>
                  </Box>
                  {simulacao && (
                    <Box sx={{ textAlign: 'right', minWidth: 190, flexShrink: 0 }}>
                      <Typography variant="caption" color="text.secondary">Receita Simulada</Typography>
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#1565c0', whiteSpace: 'nowrap' }}>
                        {formatCurrency(simulacao.receitaSimTotal)}
                      </Typography>
                      <Chip
                        size="small"
                        icon={deltaInfo.chipIcon}
                        label={deltaInfo.chipLabel}
                        color={deltaInfo.chipColor}
                        variant="outlined"
                      />
                    </Box>
                  )}
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={3}>
                  {/* Estrato IED Selector */}
                  <Grid item xs={12}>
                    <Box sx={{ px: 1, maxWidth: 400 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" fontWeight={600}>Estrato IED (Equidade)</Typography>
                        <Chip
                          label={`Estrato ${estratoIED}`}
                          size="small"
                          sx={{ backgroundColor: alpha('#7b1fa2', 0.1), color: '#7b1fa2', fontWeight: 700 }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        Determina o valor do Componente Fixo por equipe (1 = maior vulnerabilidade = maior valor)
                      </Typography>
                      <Slider
                        value={estratoIED}
                        min={1}
                        max={4}
                        step={1}
                        marks={[
                          { value: 1, label: 'I' },
                          { value: 2, label: 'II' },
                          { value: 3, label: 'III' },
                          { value: 4, label: 'IV' },
                        ]}
                        onChange={(_e, val) => setEstratoIED(val as number)}
                        sx={{ color: '#7b1fa2', '& .MuiSlider-markLabel': { fontSize: '0.7rem' } }}
                      />
                    </Box>
                    <Divider sx={{ mt: 1 }} />
                  </Grid>

                  {[
                    { key: 'esf-eap', label: 'eSF / eAP (C1-C7)' },
                    { key: 'esb', label: 'eSB (Odonto)' },
                    { key: 'emulti', label: 'eMulti' },
                  ].map(grupo => {
                    const idx = simClassificacoes[grupo.key] ?? 0;
                    const classAtual = data.classificacoes?.[grupo.key] || 'Regular';
                    return (
                      <Grid item xs={12} md={4} key={grupo.key}>
                        <Box sx={{ px: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, minHeight: 32, mb: 1 }}>
                            <Typography variant="body2" fontWeight={600} noWrap sx={{ minWidth: 0 }}>{grupo.label}</Typography>
                            <Chip
                              label={CLASSIFICACOES[idx]}
                              size="small"
                              sx={{
                                backgroundColor: alpha(CLASSIFICACAO_COLORS[CLASSIFICACOES[idx]], 0.1),
                                color: CLASSIFICACAO_COLORS[CLASSIFICACOES[idx]],
                                fontWeight: 700,
                                minWidth: 88,
                                flexShrink: 0,
                                '& .MuiChip-label': { width: '100%', textAlign: 'center' },
                              }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                            Atual: {classAtual}
                          </Typography>
                          <Slider
                            value={idx}
                            min={0}
                            max={3}
                            step={1}
                            marks={CLASSIFICACOES.map((c, i) => ({ value: i, label: c }))}
                            onChange={(_e, val) => setSimClassificacoes(prev => ({ ...prev, [grupo.key]: val as number }))}
                            sx={{
                              '& .MuiSlider-markLabel': { fontSize: '0.65rem' },
                              color: CLASSIFICACAO_COLORS[CLASSIFICACOES[idx]],
                            }}
                          />
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>

                {/* Faixa sempre presente com altura reservada: evita que o card cresca/encolha ao arrastar os sliders */}
                <Box sx={{ mt: 2, minHeight: { xs: 88, sm: 72, md: 60 }, display: 'flex', alignItems: 'stretch' }}>
                  <Alert
                    severity={deltaInfo.severity}
                    sx={{ borderRadius: 2, width: '100%', '& .MuiAlert-message': { display: 'flex', alignItems: 'center' } }}
                  >
                    {deltaInfo.mensagem}
                  </Alert>
                </Box>
              </CardContent>
            </Card>
          </>
        )}
      </Box>
    </Fade>
    {municipioAtivo && dadosContexto && (
      <AnalisePredicaoCard
        dadosContexto={dadosContexto}
        municipioId={municipioAtivo.id}
        painelId="financeiro"
        quadrimestre={quadrimestre}
        ano={ano}
        manualTrigger
      />
    )}
    <ChatIAButton contexto="saude" dadosContexto={dadosContexto} fabSx={{ bottom: 24, right: 24, top: 'auto' }} />
    </>
  );
};

export default SaudeFinanceiro;
